import { useState } from 'react'
import {
  slugifyTenant,
  type TenantSummary,
  tenantIntegrations,
  useAdminTenant,
  useAdminTenants,
  useCreateTenant,
  useDeleteTenant,
  useRevealTenantCredential,
  useUpdateTenantCredentials,
} from '../../../features/admin/use-admin-tenants'
import { VENDOR_KEYS, vendorBlock } from '../../../features/admin/vendors'
import { useOrgId } from '../../use-org-id'
import { AdminPage, adminError, VendorCard } from './admin-bits'
import { Button, ConfirmDialog, Dialog, EmptyRow, Field, Inline, inputCls, Select, td, th } from './controls'
import { AppLink, Badge, Card, mono } from './ui'

function CreateDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateTenant()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [touched, setTouched] = useState(false)
  const [banner, setBanner] = useState('')
  const effSlug = touched ? slug : slugifyTenant(name)
  return (
    <Dialog
      open
      title="Create a tenant"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            disabled={name.trim() === '' || effSlug === '' || create.isPending}
            onClick={() =>
              create.mutate({ name: name.trim(), slug: effSlug, banner: banner || undefined }, { onSuccess: onClose })
            }
          >
            {create.isPending ? 'Creating...' : 'Create tenant'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Slug" hint="Lowercase letters, numbers and dashes. Must be unique.">
        <input
          className={inputCls}
          style={mono}
          value={effSlug}
          onChange={(e) => {
            setTouched(true)
            setSlug(e.target.value)
          }}
        />
      </Field>
      <Field label="Banner">
        <Select
          value={banner}
          onChange={setBanner}
          placeholder="None"
          options={[
            ['dtlr', 'DTLR'],
            ['spc', 'SPC'],
          ]}
        />
      </Field>
      {create.error ? <Inline tone="bad">{adminError(create.error)}</Inline> : null}
    </Dialog>
  )
}

export function PorcelainAdminTenants() {
  const tenants = useAdminTenants()
  const del = useDeleteTenant()
  const [creating, setCreating] = useState(false)
  const [remove, setRemove] = useState<TenantSummary | null>(null)
  const list = tenants.data ?? []
  return (
    <AdminPage
      title="Tenants"
      subtitle="Organizations and the integrations enabled for each."
      loading={tenants.isLoading}
      error={!!tenants.error}
      right={<Button onClick={() => setCreating(true)}>Create tenant</Button>}
    >
      <Card title={`${list.length} tenant${list.length === 1 ? '' : 's'}`}>
        {list.length === 0 ? (
          <EmptyRow>No tenants yet.</EmptyRow>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Slug</th>
                  <th className={th}>Banner</th>
                  <th className={th}>Integrations</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                    <td className={`${td} font-medium`}>
                      <AppLink
                        to={`/admin/tenants/${encodeURIComponent(t.id)}`}
                        className="text-[var(--color-accent)] hover:underline"
                      >
                        {t.name}
                      </AppLink>
                    </td>
                    <td className={td} style={mono}>
                      {t.slug}
                    </td>
                    <td className={`${td} text-[var(--color-muted)]`}>{t.banner?.toUpperCase() ?? '-'}</td>
                    <td className={td}>
                      <div className="flex flex-wrap gap-1">
                        {tenantIntegrations(t).map((i) => (
                          <Badge key={i.vendor} label={i.label} tone={i.enabled ? 'ok' : 'off'} />
                        ))}
                      </div>
                    </td>
                    <td className={`${td} text-right`}>
                      <Button variant="danger" onClick={() => setRemove(t)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {creating ? <CreateDialog onClose={() => setCreating(false)} /> : null}
      <ConfirmDialog
        open={remove !== null}
        title="Delete this tenant?"
        body={<>{remove?.name} and its stored credentials will be permanently removed. This cannot be undone.</>}
        confirmLabel="Delete tenant"
        danger
        pending={del.isPending}
        error={del.error ? adminError(del.error) : null}
        onConfirm={() => remove && del.mutate({ orgId: remove.id }, { onSuccess: () => setRemove(null) })}
        onClose={() => {
          del.reset()
          setRemove(null)
        }}
      />
    </AdminPage>
  )
}

export function PorcelainAdminTenantDetail() {
  const orgId = useOrgId()
  const tenant = useAdminTenant(orgId)
  const update = useUpdateTenantCredentials()
  const reveal = useRevealTenantCredential()
  return (
    <AdminPage
      title="Tenant credentials"
      subtitle={`Integration settings for ${orgId}.`}
      loading={tenant.isLoading}
      error={!!tenant.error}
      right={
        <AppLink to="/admin/tenants" className="text-[14px] text-[var(--color-accent)] hover:underline">
          All tenants
        </AppLink>
      }
    >
      {VENDOR_KEYS.map((v) => (
        <VendorCard
          key={v}
          vendor={v}
          block={vendorBlock(tenant.data, v)}
          pending={update.isPending && update.variables?.vendor === v}
          error={update.variables?.vendor === v ? update.error : null}
          onSave={(body) => update.mutate({ orgId, ...body })}
          reveal={(vendor, field) => reveal.mutateAsync({ orgId, vendor, field })}
        />
      ))}
    </AdminPage>
  )
}
